import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link as RouterLink } from "react-router-dom";
import { IErrors, PageState } from "../../api/Types";
import { EQuoteStatus } from "../../api/Quote/EQuoteStatus";
import { UpdateQuoteForm } from "../../api/Quote/UpdateQuoteForm";
import { QuoteLineItem } from "../../api/Quote/QuoteLineItem";
import { RootState } from "../../app/rootReducer";
import { fetchDeductions } from "../../store/DeductionsSlice";
import { customerTextUpdated, fetchBalance, fetchQuote, removeAddonQuote } from "../../store/QuoteSlice";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import VisibilityIcon from "@material-ui/icons/Visibility";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import AddIcon from "@material-ui/icons/Add";
import GetAppIcon from "@material-ui/icons/GetApp";
import { CustomerDetails } from "./CustomerDetails";
import { QuoteDetails } from "./QuoteDetails";
import { EmailModal } from "./EmailModal";
import { toast } from "react-toastify";
import { PermissionDiscountWrite, PermissionQuoteWrite } from "../../app/consts";
import { fetchAccount } from "../../store/AccountSlice";
import { ExtraItemModal } from "./ExtraItemModal";
import { NewlineDisplay } from "../../components/NewlineDisplay";
import {
  allowManagerEdits,
  hasGrantDeductedAtSource,
  isLocalHeroes,
  quoteBalanceOutstanding,
  quoteInternalPayment
} from "../../app/utils";
import { AddonForm } from "../../api/Addon/AddonApi";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format } from "date-fns";
import { toNumber } from "lodash";
import { useAuth, Log, boxShadow, Loader } from "swyft-react-core";
import { downloadQuotePdf, updateQuote, updateQuoteStatus } from "../../api/Quote/QuoteApi";

import { ESoldOnOptions } from "./constants";
import "./styles.css";
import { LocalHeroesModal } from "./LocalHeroesModal";
import { shouldRedirectQuote, getInternalQuoteRedirectUrl } from "../../app/redirects";

const styles = makeStyles((theme) => ({
  root: {
    // backgroundColor: "rgba(75,84,98,0.05)",
  },
  actions: {
    "& button": {
      marginLeft: "1rem"
    }
  },
  button: {
    margin: theme.spacing(1)
  }
}));

interface State {
  emailModalOpen: boolean;
  localHeroesModalOpen: boolean;
  extraItemModalOpen: boolean;
  editingNotes: boolean;
  editingCrm: boolean;
  savingNotes: boolean;
  savingCrmFields: boolean;
  notes: string;
  amount: number | null;
  soldOn: string | null;
  requestedInstallationDate: Date | null;
  contextAnchorEl: null | HTMLElement;
  showApproveConfirmationModal: boolean;
  amountError: string | null;
  grantPortalSetUp: boolean;
}

type Props = RouteComponentProps<{ id: string }>;
export const EditQuotePage = ({ match }: Props) => {
  Log.debug("EditQuotePage");

  const dispatch = useDispatch();
  const classes = styles();

  const [state, setState] = useState<State>({
    emailModalOpen: false,
    localHeroesModalOpen: false,
    extraItemModalOpen: false,
    editingNotes: false,
    editingCrm: false,
    savingNotes: false,
    savingCrmFields: false,
    notes: "",
    contextAnchorEl: null,
    amount: null,
    soldOn: "",
    requestedInstallationDate: null,
    showApproveConfirmationModal: false,
    amountError: null,
    grantPortalSetUp: false
  });

  const { pageState, error, quote } = useSelector((state: RootState) => state.quote);

  const { isLoading: deductionsIsLoading, error: deductionsError } = useSelector(
    (state: RootState) => state.deductions
  );

  const {
    pageState: accountPageState,
    account,
    error: accountError
  } = useSelector((state: RootState) => state.account);

  const { hasPermission: checkPermission } = useAuth();

  const quoteId = match.params.id;

  useEffect(() => {
    dispatch(fetchQuote(quoteId));
  }, [quoteId, dispatch]);

  useEffect(() => {
    if (checkPermission(PermissionDiscountWrite)) {
      dispatch(fetchDeductions());
    }
  }, [dispatch, checkPermission]);

  useEffect(() => {
    if (quote) {
      dispatch(fetchAccount(quote.customerId));
    }
  }, [quote, dispatch]);

  useEffect(() => {
    dispatch(fetchBalance(quoteId));
  }, [quoteId, dispatch]);

  const setQuoteStatus = async (quoteId: string, status: EQuoteStatus) => {
    await updateQuoteStatus(quoteId, status)
      .then(() => {
        dispatch(fetchQuote(quoteId));
        toast.success(`Quote ${status}`);
      })
      .catch((err) => {
        console.log(err);
        const errors: IErrors = {};
        Object.keys(err.errors).forEach((key: string) => {
          const fieldName = key.charAt(0).toLowerCase() + key.substring(1);
          errors[fieldName] = err.errors[key][0];
        });
        throw errors;
      });
  };

  const handleSaveNotes = async () => {
    setState({ ...state, savingNotes: true });

    var quoteRequest = {
      id: quote?.id,
      customerText: state.notes
    } as UpdateQuoteForm;

    await updateQuote(quoteRequest)
      .then(() => {
        dispatch(customerTextUpdated(state.notes));
      })
      .catch((err) => {
        console.log(err);
        const errors: IErrors = {};
        Object.keys(err.errors).forEach((key: string) => {
          const fieldName = key.charAt(0).toLowerCase() + key.substring(1);
          errors[fieldName] = err.errors[key][0];
        });
        throw errors;
      })
      .finally(() => {
        setState({ ...state, editingNotes: false, savingNotes: false });
      });
  };

  const handleApproveQuote = async () => {
    if (state.savingCrmFields) {
      return;
    }

    if (state.amount != null) {
      if (state.amount < 0.01) {
        setState({ ...state, savingCrmFields: false, amountError: "Payment value can not be zero or negative" });
        return;
      } else if (state.amount > quoteBalanceOutstanding(quote!)) {
        setState({ ...state, savingCrmFields: false, amountError: "Payment can not exceed outstanding balance" });
        return;
      }
    }

    setState({ ...state, savingCrmFields: true, amountError: null });

    var quoteRequest = {
      id: quote?.id,
      amount: state.amount,
      requestedInstallationDate: state.requestedInstallationDate,
      soldOn: state.soldOn,
      status: EQuoteStatus.Approved
    } as UpdateQuoteForm;

    await updateQuote(quoteRequest)
      .then(() => {
        dispatch(fetchQuote(quoteId));
      })
      .catch((err) => {
        console.log(err);
        const errors: IErrors = {};
        Object.keys(err.errors).forEach((key: string) => {
          const fieldName = key.charAt(0).toLowerCase() + key.substring(1);
          errors[fieldName] = err.errors[key][0];
        });
        throw errors;
      })
      .finally(() => {
        setState({ ...state, editingCrm: false, savingCrmFields: false });
      });
  };

  const handleRemove = function (addon: QuoteLineItem) {
    dispatch(
      removeAddonQuote(quoteId, {
        addonId: addon.productId
      } as AddonForm)
    );
    toast.success(addon.name + " has been removed");
  };

  if (
    !quote ||
    !account ||
    pageState === PageState.Loading ||
    deductionsIsLoading ||
    accountPageState === PageState.Loading
  ) {
    return <Loader />;
  }

  if (pageState === PageState.Error || deductionsError || error || accountError) {
    return <div>Error</div>;
  }

  if (shouldRedirectQuote(quote)) {
    window.location.href = getInternalQuoteRedirectUrl(quote.id);
    return <Loader />;
  }

  const handleEmailClick = () => {
    if (quote.status === EQuoteStatus.Credited) {
      toast.warning("Quote has been credited");
    } else if (quote.status === EQuoteStatus.Pending) {
      toast.warning("Quote pending - awaiting job completion");
    } else {
      if (isLocalHeroes(quote)) {
        setState({ ...state, localHeroesModalOpen: true });
      } else {
        setState({ ...state, emailModalOpen: true });
      }
    }
  };

  const handleAddExtra = () => {
    if (quote.status === EQuoteStatus.Approved) {
      toast.warning("Quote has already been approved");
    } else if (quote.status === EQuoteStatus.Pending) {
      toast.warning("Quote pending - awaiting job completion");
    } else {
      setState({ ...state, extraItemModalOpen: true });
    }
  };

  const handleDownload = async () => {
    var blob = await downloadQuotePdf(quote.id);

    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${quote.name}.pdf`);

    document.body.appendChild(link);
    link.click();
    link.parentNode!.removeChild(link);
  };

  const handleContextOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setState({ ...state, contextAnchorEl: event.currentTarget });
  };

  const handleContextClose = () => {
    setState({ ...state, contextAnchorEl: null });
  };

  const handleCreditQuote = () => {
    setQuoteStatus(quoteId, EQuoteStatus.Credited);
    handleContextClose();
  };

  const handleReactivateQuote = () => {
    setQuoteStatus(quoteId, quote.sent === undefined || quote.sent === null ? EQuoteStatus.Draft : EQuoteStatus.Sent);
    handleContextClose();
  };

  const HeadSection = (
    <>
      <Box display="flex" my={3}>
        <Box flexGrow={1}>
          <Typography variant="h2">Customer Quote</Typography>
        </Box>
        <div className={classes.actions}>
          {!isLocalHeroes(quote) && (
            <IconButton component={RouterLink} to={`/quote/${quote.id}`} color="secondary" title="Preview">
              <VisibilityIcon color="secondary" />
            </IconButton>
          )}

          {checkPermission(PermissionQuoteWrite) && (
            <React.Fragment>
              <IconButton
                color="secondary"
                onClick={() => {
                  handleEmailClick();
                }}
                title="Email"
                disabled={
                  quote.status === EQuoteStatus.Credited ||
                  quote.status === EQuoteStatus.Voided ||
                  quote.status === EQuoteStatus.Inactive
                }
              >
                <MailOutlineIcon />
              </IconButton>

              <IconButton
                color="secondary"
                onClick={() => handleAddExtra()}
                title="Add additional items"
                disabled={!allowManagerEdits(quote)}
              >
                <AddIcon />
              </IconButton>

              {!isLocalHeroes(quote) && (
                <IconButton
                  color="secondary"
                  onClick={() => handleDownload()}
                  // href={`/api/store/email/quote/${quote.id}/pdf`}
                  disabled={
                    quote.status === EQuoteStatus.Credited ||
                    quote.status === EQuoteStatus.Paid ||
                    quote.status === EQuoteStatus.Payment ||
                    quote.status === EQuoteStatus.Voided ||
                    quote.status === EQuoteStatus.Inactive
                  }
                  title="Download"
                >
                  <GetAppIcon />
                </IconButton>
              )}

              {!isLocalHeroes(quote) && (
                <>
                  <IconButton color="secondary" onClick={handleContextOpen}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    id="simple-menu"
                    anchorEl={state.contextAnchorEl}
                    keepMounted
                    open={Boolean(state.contextAnchorEl)}
                    onClose={handleContextClose}
                  >
                    <MenuItem
                      onClick={handleCreditQuote}
                      disabled={
                        quote.status !== EQuoteStatus.Approved &&
                        quote.status !== EQuoteStatus.Payment &&
                        quote.status !== EQuoteStatus.Paid
                      }
                    >
                      Credit Quote
                    </MenuItem>
                    {quote.status === EQuoteStatus.Inactive && (
                      <MenuItem onClick={handleReactivateQuote}>Reactivate Quote</MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </React.Fragment>
          )}
        </div>
      </Box>
      {isLocalHeroes(quote) && (
        <Box boxShadow={boxShadow} padding={2} mb={3} textAlign="center" border="1px solid" borderColor="red">
          <Typography variant="h4">Local Heroes Quote</Typography>
        </Box>
      )}
    </>
  );

  const NotesSection = (
    <Box boxShadow={boxShadow} padding={3} mb={3}>
      <Typography variant="h2" paragraph>
        Assessment Comments
      </Typography>
      {state.editingNotes ? (
        <div>
          <Box py={2} px={1}>
            <TextField
              fullWidth
              id="notes"
              label="Notes"
              multiline
              rows={5}
              value={state.notes}
              onChange={(e) => setState({ ...state, notes: e.currentTarget.value })}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={(_) =>
                setState({
                  ...state,
                  editingNotes: false,
                  notes: quote.customerText ?? ""
                })
              }
              className={classes.button}
            >
              Cancel
            </Button>
            <Button variant="contained" color="secondary" onClick={(_) => handleSaveNotes()} className={classes.button}>
              Save
            </Button>
          </Box>
        </div>
      ) : !quote.customerText || quote.customerText === "" ? (
        checkPermission(PermissionQuoteWrite) &&
        allowManagerEdits(quote) && (
          <Box display="flex" justifyContent="flex-end">
            <Button
              onClick={(_) => {
                setState({ ...state, editingNotes: true });
              }}
              variant="contained"
              color="secondary"
            >
              Add Comments
            </Button>
          </Box>
        )
      ) : (
        <div>
          <Box pb={2} px={1}>
            <NewlineDisplay text={quote.customerText} />
          </Box>
          {checkPermission(PermissionQuoteWrite) && allowManagerEdits(quote) && (
            <Box display="flex" justifyContent="flex-end">
              <Button
                onClick={(_) => {
                  setState({
                    ...state,
                    editingNotes: true,
                    notes: quote.customerText ?? ""
                  });
                }}
                variant="contained"
                color="secondary"
              >
                Edit Comments
              </Button>
            </Box>
          )}
        </div>
      )}
    </Box>
  );

  const internalPayment = quoteInternalPayment(quote);

  const CrmEditingSection = (
    <div>
      <Box py={2} px={1}>
        <Box>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              disableToolbar
              variant="inline"
              format="dd/MM/yyyy"
              margin="normal"
              id="date-picker-inline"
              label="Requestead Installation Date"
              value={state.requestedInstallationDate}
              onChange={(date: Date | null) => {
                setState({ ...state, requestedInstallationDate: date });
              }}
              KeyboardButtonProps={{
                "aria-label": "change date"
              }}
            />
          </MuiPickersUtilsProvider>
        </Box>
        <Box mt={2}>
          <TextField
            id="amount"
            label="Amount"
            variant="outlined"
            type="number"
            value={state.amount ?? ""}
            onChange={(e) => {
              setState({ ...state, amount: toNumber(e.currentTarget.value) });
            }}
          />
        </Box>
        <Box mt={2}>
          <Select
            className="sold-on-select"
            labelId="select-discount-label"
            label="SoldOn"
            id="soldOn"
            onChange={(e: any) => {
              setState({ ...state, soldOn: e.target?.value || "" });
            }}
            value={state.soldOn ? state.soldOn : quote.soldOn ?? ""}
          >
            {Object.values(ESoldOnOptions).map((value) => {
              return (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              );
            })}
          </Select>
        </Box>
        <Box mt={2} hidden={!hasGrantDeductedAtSource(quote)}>
          <FormControl component="fieldset">
            <FormGroup aria-label="position" row>
              <FormControlLabel
                value="end"
                control={
                  <Checkbox
                    checked={state.grantPortalSetUp}
                    onChange={() => setState({ ...state, grantPortalSetUp: !state.grantPortalSetUp })}
                    color="secondary"
                    inputProps={{ "aria-label": "secondary checkbox" }}
                  />
                }
                label="SEAI Portal Set Up"
                labelPlacement="end"
              />
            </FormGroup>
          </FormControl>
        </Box>
        {state.amountError && (
          <Typography variant="caption" color="error">
            {state.amountError}
          </Typography>
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="secondary"
          onClick={handleApproveQuote}
          className={classes.button}
          disabled={
            !state.amount ||
            !state.requestedInstallationDate ||
            !state.soldOn ||
            (hasGrantDeductedAtSource(quote) && !state.grantPortalSetUp)
          }
        >
          Approve Quote
        </Button>
      </Box>
    </div>
  );

  const CrmViewSection = (
    <div>
      <Box pb={2} px={1}>
        <Typography variant="body1">
          Requested installation date :{" "}
          {quote.requestedInstallationDate === null
            ? ""
            : format(new Date(quote.requestedInstallationDate), "dd/MM/yyyy")}
        </Typography>
        <Typography variant="body1">Amount : {internalPayment?.amount}</Typography>
        <Typography variant="body1">Sold On : {quote.soldOn}</Typography>
      </Box>
    </div>
  );

  const CrmSection = (
    <Box boxShadow={boxShadow} padding={3} mb={3}>
      <Typography variant="h2" paragraph>
        Quote Approval
      </Typography>
      {quote.status === EQuoteStatus.Sent ? CrmEditingSection : CrmViewSection}
    </Box>
  );

  return (
    <div className={classes.root}>
      <EmailModal
        quote={quote}
        account={account}
        isOpen={state.emailModalOpen}
        onClose={() => setState({ ...state, emailModalOpen: false })}
      />

      <LocalHeroesModal
        quote={quote}
        isOpen={state.localHeroesModalOpen}
        onClose={() => setState({ ...state, localHeroesModalOpen: false })}
      />

      <ExtraItemModal
        quote={quote}
        account={account}
        isOpen={state.extraItemModalOpen}
        onClose={() => setState({ ...state, extraItemModalOpen: false })}
      />

      <Container maxWidth="lg">
        {HeadSection}
        <CustomerDetails account={account} quote={quote} />
        <QuoteDetails quote={quote} onRemoveAddon={handleRemove} />
        {NotesSection}
        {quote.status !== EQuoteStatus.Pending &&
          quote.status !== EQuoteStatus.Draft &&
          !isLocalHeroes(quote) &&
          CrmSection}
      </Container>
    </div>
  );
};
